/**
 * Portals via MUI Popper so the menu can escape ancestor `overflow: hidden`
 * (e.g. DataGrid cells). Modus DropdownButton cannot be used — it renders inline.
 */
import { ClickAwayListener, Popper } from "@mui/material";
import type { DropdownMenu } from "@trimble/modus-cmx";
import { Button, Icon, Menu } from "@trimble/modus-cmx";
import type React from "react";
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

export type OverflowMenuRef = {
  menuVisible: boolean;
};

type Placement = "bottom-start" | "bottom-end" | "top-start" | "top-end";

type OverflowMenuProps = Pick<
  React.ComponentProps<typeof DropdownMenu>,
  "buttonColor" | "buttonVariant" | "disabled" | "buttonAriaLabel" | "children"
> & {
  placement?: Placement;
  iconName?: React.ComponentProps<typeof Icon>["name"];
  iconVariant?: React.ComponentProps<typeof Icon>["variant"];
  iconStyle?: React.CSSProperties;
  /**
   * Popper `[skidding, distance]`. Negative skidding on `*-end` aligns the
   * menu with the icon glyph rather than the padded button box.
   */
  offset?: [number, number];
  /**
   * When true (default), hide the trigger unless a `modus-wc-menu-item` is
   * present. Set false for custom panel content (e.g. Recently Viewed).
   */
  requireMenuItems?: boolean;
  onOpenChange?: (open: boolean) => void;
  ref?: React.Ref<OverflowMenuRef>;
};

const DEFAULT_OFFSET: [number, number] = [0, 10];

const popperSx = {
  zIndex: 1300,
  "& modus-wc-menu .modus-wc-menu": {
    borderRadius: "var(--modus-wc-border-radius-2xl)",
    overflow: "hidden",
  },
};

const OverflowMenu = (props: OverflowMenuProps) => {
  const {
    children,
    buttonAriaLabel,
    disabled = false,
    iconName = "more_vertical",
    iconVariant,
    iconStyle,
    offset = DEFAULT_OFFSET,
    requireMenuItems = true,
    onOpenChange,
    ref,
    placement = "bottom-start",
    buttonColor,
    buttonVariant,
  } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  const onOpenChangeRef = useRef(onOpenChange);
  const [open, setOpen] = useState(false);
  const [hasContent, setHasContent] = useState(!requireMenuItems);

  useImperativeHandle(
    ref,
    () => ({
      get menuVisible() {
        return open;
      },
      set menuVisible(value: boolean) {
        setOpen(value);
      },
    }),
    [open]
  );

  useEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  });

  // Query real menu items so wrappers / gated-null children don't keep the trigger visible.
  // Ref callback (not useEffect) so the portaled Popper node is observed as soon as it commits.
  const setContentRef = useCallback(
    (el: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (!el) return;

      if (!requireMenuItems) {
        setHasContent(true);
        return;
      }

      const update = () => {
        const hasItems = el.querySelector("modus-wc-menu-item") !== null;
        setHasContent(hasItems);
        if (!hasItems) {
          setOpen(false);
        }
      };
      update();

      const observer = new MutationObserver(update);
      observer.observe(el, { childList: true, subtree: true });
      observerRef.current = observer;
    },
    [requireMenuItems]
  );

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, []);

  useEffect(() => {
    onOpenChangeRef.current?.(open);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    const gridScroller = anchorEl?.closest(".MuiDataGrid-virtualScroller");
    const handleGridScroll = () => {
      setOpen(false);
    };
    document.addEventListener("keydown", handler);
    gridScroller?.addEventListener("scroll", handleGridScroll);
    return () => {
      document.removeEventListener("keydown", handler);
      gridScroller?.removeEventListener("scroll", handleGridScroll);
    };
  }, [open, anchorEl]);

  const handleToggle = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
  };

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    // Ignore trigger clicks so ClickAway doesn't race the toggle and immediately re-close.
    const target = event.target;
    if (target instanceof Node && anchorEl?.contains(target)) {
      return;
    }
    setOpen(false);
  };

  return (
    <>
      <div
        ref={setAnchorEl}
        style={{
          display: hasContent || disabled ? "inline-flex" : "none",
          pointerEvents: disabled ? "none" : undefined,
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <Button
          size="md"
          variant={buttonVariant ?? "borderless"}
          color={buttonColor}
          shape="circle"
          aria-label={buttonAriaLabel}
          aria-expanded={open}
          aria-haspopup="true"
          onButtonClick={handleToggle}
        >
          <Icon
            name={iconName}
            variant={iconVariant}
            size="md"
            style={iconStyle}
          />
        </Button>
      </div>
      <Popper
        open={open && hasContent && !disabled}
        anchorEl={anchorEl}
        placement={placement}
        keepMounted
        modifiers={[
          { name: "offset", options: { offset } },
          { name: "flip", enabled: true },
          { name: "preventOverflow", enabled: true, options: { padding: 8 } },
        ]}
        sx={popperSx}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <div>
            <Menu bordered size="md">
              <div ref={setContentRef}>{children}</div>
            </Menu>
          </div>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default OverflowMenu;
